import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#00838f", "#c62828"];

export default function ComparePage(props) {
  const { apiAddr, onClose } = props;
  const navigate = useNavigate();
  const handleBack = onClose ? () => onClose() : () => navigate("/");
  const [dsList, setDsList] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [compareLoading, setCompareLoading] = useState(false);
  const [error, setError] = useState("");
  const [chartData, setChartData] = useState([]);

  const prefix = (apiAddr || "").replace(/\/$/, "");

  useEffect(() => {
    if (!prefix) return;
    setLoading(true);
    setError("");
    fetch(`${prefix}/get/dslist`)
      .then((r) => r.json())
      .then((arr) => {
        const list = Array.isArray(arr) ? arr : [];
        setDsList(list.map((d) => ({ dsid: (d.dsnum || d.dsid || "").toString().trim(), name: (d.dsnm || d.dsnum || "").toString().trim() })).filter((d) => d.dsid));
      })
      .catch((e) => {
        setError(e.message || "재난 목록을 불러오지 못했습니다.");
        setDsList([]);
      })
      .finally(() => setLoading(false));
  }, [prefix]);

  const handleToggle = (dsid) => {
    setSelected((prev) => ({ ...prev, [dsid]: !prev[dsid] }));
  };

  const handleCompare = useCallback(async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length < 2) {
      setError("비교하려면 재난을 2개 이상 선택해 주세요.");
      return;
    }
    setCompareLoading(true);
    setError("");
    try {
      const summaries = await Promise.all(
        ids.map((dsid) =>
          fetch(`${prefix}/get/dashboardSummary?dsid=${encodeURIComponent(dsid)}`).then((r) => r.json())
        )
      );
      const names = ids.map((id) => dsList.find((d) => d.dsid === id)?.name || id);
      const data = ids.map((id, i) => {
        const s = summaries[i] || {};
        const sb = s.signboard || {};
        const rc = s.rdvCount || {};
        const totl = Number(sb.totl) || 0;
        const supportTotal = (Number(rc.fireCount) || 0) + (Number(rc.otherCount) || 0);
        return {
          name: (names[i] || id).slice(0, 12),
          동원: totl,
          지원인력: supportTotal,
        };
      });
      setChartData(data);
    } catch (e) {
      setError(e.message || "비교 데이터를 불러오지 못했습니다.");
      setChartData([]);
    } finally {
      setCompareLoading(false);
    }
  }, [prefix, selected, dsList]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Box sx={{ p: 2, maxWidth: 1000, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
          홈
        </Button>
        <Typography variant="h6">기관·재난별 비교</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              비교할 재난을 2개 이상 선택한 뒤 [비교하기]를 누르세요.
            </Typography>
            <FormGroup row sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {dsList.map((d, i) => (
                <FormControlLabel
                  key={d.dsid}
                  control={
                    <Checkbox
                      checked={!!selected[d.dsid]}
                      onChange={() => handleToggle(d.dsid)}
                      size="small"
                    />
                  }
                  label={<span title={d.dsid}>{d.name || d.dsid}</span>}
                />
              ))}
            </FormGroup>
            <Button
              startIcon={compareLoading ? <CircularProgress size={18} /> : <BarChartIcon />}
              onClick={handleCompare}
              disabled={compareLoading || selectedCount < 2}
              variant="contained"
              sx={{ mt: 1 }}
            >
              비교하기
            </Button>
          </Paper>

          {chartData.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                동원 규모 · 지원인력 비교
              </Typography>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="동원" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="지원인력" fill={COLORS[1]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
